import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie, X } from "lucide-react";

// ---------------------------------------------------------------------------
// Postmoro cookie / analytics consent banner.
//
// Design:
//   - Denied by default (see the `gtag('consent', 'default', ...)` call in
//     public/index.html which is what actually enforces the denied state).
//   - Small bottom-of-viewport card, non-modal, does not block any part of
//     the app. Essential functionality works whether the user accepts,
//     declines, or ignores the banner.
//   - The user's choice is persisted in localStorage under a single key so
//     the banner never reappears once a choice is made.
//   - On subsequent page loads: read the stored choice, and if it was
//     "granted" call `gtag('consent', 'update', {analytics_storage:
//     'granted'})` early enough that GA4 accepts the flip.
//
// PII policy stays intact:
//   - Even after Accept, the URL sanitiser in public/index.html strips
//     search + hash from every page_view.
//   - We never call gtag with user_id, email, name, or any Postmoro
//     account identifier — the banner only toggles storage consent.
// ---------------------------------------------------------------------------

const STORAGE_KEY = "postmoro_analytics_consent"; // values: "granted" | "denied" | absent
const BANNER_TESTID = "consent-banner";

function readStoredConsent() {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "granted" || v === "denied") return v;
  } catch (_) {
    // localStorage can throw in Safari private mode; treat as "no choice".
  }
  return null;
}

function writeStoredConsent(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch (_) {
    // Best-effort; if storage is unavailable the banner will simply
    // reappear on next load, which is acceptable.
  }
}

function updateGtagConsent(state) {
  try {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: state,
      });
    }
  } catch (_) {
    /* never break the app because of analytics */
  }
}

/**
 * Renders the consent banner (only if no prior choice is stored) and
 * replays the stored choice to gtag on mount for returning visitors.
 * Safe to mount inside <BrowserRouter> at the app root.
 */
export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const prior = readStoredConsent();
    if (prior === "granted") {
      // Returning visitor who previously accepted — re-grant on load
      // so GA4 doesn't stay denied for the rest of the session.
      updateGtagConsent("granted");
      setVisible(false);
      return;
    }
    if (prior === "denied") {
      // Returning visitor who previously declined — no gtag update
      // needed, consent stays at the denied default.
      setVisible(false);
      return;
    }
    // No prior choice — surface the banner.
    setVisible(true);
  }, []);

  const handleAccept = () => {
    writeStoredConsent("granted");
    updateGtagConsent("granted");
    setVisible(false);
  };

  const handleDecline = () => {
    writeStoredConsent("denied");
    // No gtag update needed — consent already defaults to denied.
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie and analytics consent"
      data-testid="consent-banner"
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-md z-[100] rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)] backdrop-blur"
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)] shrink-0">
            <Cookie className="h-5 w-5" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-sm font-semibold text-[color:var(--text-1)]" data-testid="consent-banner-title">
              Analytics cookies
            </h2>
            <p className="mt-1 text-xs text-[color:var(--text-2)] leading-relaxed" data-testid="consent-banner-body">
              Postmoro uses Google Analytics to see which pages help visitors
              find us. It stores a cookie on your device only if you agree.
              We never send your name, email, or the content you create.{" "}
              <Link
                to="/privacy"
                className="underline underline-offset-2 hover:text-[color:var(--text-1)]"
                data-testid="consent-banner-privacy-link"
              >
                Read our privacy policy
              </Link>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={handleDecline}
            aria-label="Decline analytics cookies"
            data-testid="consent-banner-close"
            className="text-[color:var(--text-3)] hover:text-[color:var(--text-1)] shrink-0 -m-1 p-1 rounded"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDecline}
            className="text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
            data-testid="consent-banner-decline"
          >
            Decline
          </Button>
          <Button
            size="sm"
            onClick={handleAccept}
            className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
            data-testid="consent-banner-accept"
          >
            Accept analytics
          </Button>
        </div>
      </div>
    </div>
  );
}

// Exported for unit tests only — do not import from app code.
export const __CONSENT_INTERNALS = {
  STORAGE_KEY,
  readStoredConsent,
  writeStoredConsent,
  updateGtagConsent,
};
